import React from "react";

import {
  FaBookOpen,
  FaClone,
  FaEdit,
  FaGlobeEurope,
  FaLayerGroup,
  FaPenFancy,
  FaRocket,
  FaTrashAlt,
} from "react-icons/fa";

import TutorialStatusBadge from "./TutorialStatusBadge";

import {
  formatTutorialDate,
  getTutorialScopeLabel,
} from "../utils/tutorialAdminUtils";

const ACTION =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50";

export default function TutorialCard({
  tutorial,
  busy = false,

  onOpen,
  onEdit,
  onPublish,
  onDuplicate,
  onDelete,
}) {
  return (
    <article className="group relative overflow-hidden rounded-[26px] border border-slate-200/80 bg-white shadow-[0_20px_55px_-28px_rgba(15,23,42,0.40)] transition hover:-translate-y-0.5 hover:shadow-[0_26px_70px_-30px_rgba(79,70,229,0.40)] dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />

      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-lg text-white shadow-lg shadow-indigo-500/20">
              <FaBookOpen />
            </div>

            <div className="min-w-0">
              <h3 className="line-clamp-2 text-lg font-semibold text-slate-900 dark:text-slate-50">
                {tutorial.titre}
              </h3>

              <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                {tutorial.sous_titre ||
                  "Aucun sous-titre renseigné."}
              </p>
            </div>
          </div>

          <TutorialStatusBadge
            status={tutorial.statut}
          />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <InfoBlock
            icon={<FaLayerGroup />}
            label="Application"
            value={tutorial.app_key}
          />

          <InfoBlock
            icon={<FaGlobeEurope />}
            label="Portée"
            value={getTutorialScopeLabel(
              tutorial
            )}
          />

          <InfoBlock
            label="Version"
            value={`v${tutorial.version}`}
          />

          <InfoBlock
            label="Langue"
            value={tutorial.locale || "fr-FR"}
          />
        </div>

        <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
          Dernière modification :{" "}
          <span className="font-medium text-slate-700 dark:text-slate-200">
            {formatTutorialDate(
              tutorial.updated_at
            )}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onOpen(tutorial)}
            className={`${ACTION} col-span-2 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white hover:opacity-95`}
          >
            <FaPenFancy />
            Gérer le contenu
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => onEdit(tutorial)}
            className={`${ACTION} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700`}
          >
            <FaEdit />
            Informations
          </button>

          {tutorial.statut !== "publie" ? (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                onPublish(tutorial)
              }
              className={`${ACTION} border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900/50 dark:bg-emerald-500/10 dark:text-emerald-300`}
            >
              <FaRocket />
              Publier
            </button>
          ) : (
            <div className="flex items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-500/10 dark:text-emerald-300">
              Version active
            </div>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() =>
              onDuplicate(tutorial)
            }
            className={`${ACTION} border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-900/50 dark:bg-violet-500/10 dark:text-violet-300`}
          >
            <FaClone />
            Dupliquer
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() =>
              onDelete(tutorial)
            }
            className={`${ACTION} border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-500/10 dark:text-rose-300`}
          >
            <FaTrashAlt />
            Supprimer
          </button>
        </div>
      </div>
    </article>
  );
}

function InfoBlock({
  icon,
  label,
  value,
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
        {icon}
        {label}
      </div>

      <div className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
        {value || "—"}
      </div>
    </div>
  );
}